import React from 'react';
import { Plus, X, Download, Trash2, Search } from 'lucide-react';

const InventoryModals = ({
  showNewItemModal,
  setShowNewItemModal,
  handleCreateItem,
  newItem,
  setNewItem,
  newItemVersion,
  setNewItemVersion,
  newItemComponentCount,
  items,
  handleNewItemComponentItemChange,
  handleAddNewItemComponent,
  handleUpdateNewItemComponent,
  newItemComponents,
  newItemComponentVersionMap,
  newItemComponentVersionLoading,
  handleRemoveNewItemComponent,
  showQuickStockModal,
  setShowQuickStockModal,
  handleQuickStockCreate,
  quickStock,
  setQuickStock,
  loading,
  clients,
  projects,
  showExistingStockModal,
  setShowExistingStockModal,
  handleCreateStock,
  stockFormItemId,
  setStockFormItemId,
  stockFormVersions,
  newStock,
  setNewStock,
  statusOptions,
  selectedChildStockIds,
  setSelectedChildStockIds,
  filteredLinkableStockItems,
  loadingLinkable,
  linkableSearch,
  setLinkableSearch,
  getStatusLabel,
  showQrModal,
  qrStockItem,
  closeQrModal,
  qrLoading,
  qrDataUrl,
  getStockShareUrl,
  showStockEditModal,
  editingStockItem,
  closeStockEditModal,
  handleUpdateStock,
  stockEdits,
  setStockEdits,
  handleDeleteStock,
}) => {
  const [itemSearch, setItemSearch] = React.useState('');
  const [itemDropdownOpen, setItemDropdownOpen] = React.useState(false);

  React.useEffect(() => {
    if (!showExistingStockModal) {
      setItemSearch('');
      setItemDropdownOpen(false);
      return;
    }
    if (!stockFormItemId) {
      setItemSearch('');
      return;
    }
    const selectedItem = items.find(
      (item) => String(item.id) === String(stockFormItemId),
    );
    setItemSearch(selectedItem?.name || '');
  }, [showExistingStockModal, stockFormItemId, items]);

  const normalizedItemSearch = itemSearch.trim().toLowerCase();
  const filteredItems = React.useMemo(() => {
    if (!normalizedItemSearch) return items;
    return items.filter((item) => {
      const haystack = [item.name, item.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedItemSearch);
    });
  }, [items, normalizedItemSearch]);

  const openItemDropdown = () => setItemDropdownOpen(true);
  const closeItemDropdown = React.useCallback(() => setItemDropdownOpen(false), []);

  const handleItemInputChange = (event) => {
    setItemSearch(event.target.value);
    setItemDropdownOpen(true);
  };

  const handleItemSelect = (item) => {
    setStockFormItemId(String(item.id));
    setItemSearch(item.name || '');
    closeItemDropdown();
  };

  const renderComponentList = (components, parentId = null, depth = 0) => {
    const hasComponents = Array.isArray(components) && components.length > 0;
    return (
      <div className={`space-y-3 ${depth > 0 ? 'border-l border-slate-200/70 pl-4' : ''}`}>
        {hasComponents ? (
          components.map((component) => {
            const itemVersions = component.child_item_id
              ? newItemComponentVersionMap[String(component.child_item_id)] || []
              : [];
            const isLoadingVersions = component.child_item_id
              ? Boolean(newItemComponentVersionLoading[String(component.child_item_id)])
              : false;
            return (
              <div
                key={component.id}
                className="rounded-lg border border-slate-200/70 bg-white px-3 py-3 text-xs"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400">
                    {component.type === 'new' ? 'Sous-item a creer' : 'Sous-item existant'}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveNewItemComponent(component.id)}
                    className="text-[11px] text-rose-600 hover:text-rose-700"
                  >
                    Supprimer
                  </button>
                </div>
                {component.type === 'existing' ? (
                  <div className="mt-2 space-y-2">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                      <select
                        value={component.child_item_id}
                        onChange={(e) =>
                          handleNewItemComponentItemChange(component.id, e.target.value)
                        }
                        className="rounded border px-2 py-2 text-sm"
                      >
                        <option value="">Choisir un item</option>
                        {items.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={component.child_version_id}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            child_version_id: e.target.value,
                          }))
                        }
                        className="rounded border px-2 py-2 text-sm"
                        disabled={!component.child_item_id || isLoadingVersions}
                      >
                        <option value="">
                          {isLoadingVersions ? 'Chargement...' : 'Version (optionnel)'}
                        </option>
                        {itemVersions.map((versionOption) => (
                          <option key={versionOption.id} value={versionOption.id}>
                            V{versionOption.version_number} {versionOption.label || ''}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={component.quantity}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            quantity: e.target.value,
                          }))
                        }
                        className="rounded border px-2 py-2 text-sm"
                        placeholder="Quantite"
                      />
                      <input
                        type="text"
                        value={component.notes || ''}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        className="rounded border px-2 py-2 text-sm"
                        placeholder="Note (optionnel)"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 space-y-3">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <input
                        type="text"
                        placeholder="Nom du sous-item"
                        value={component.new_item?.name || ''}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            new_item: {
                              ...prev.new_item,
                              name: e.target.value,
                            },
                          }))
                        }
                        className="rounded border px-3 py-2 text-sm"
                        required
                      />
                      <input
                        type="text"
                        placeholder="Lien (optionnel)"
                        value={component.new_item?.purchase_url || ''}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            new_item: {
                              ...prev.new_item,
                              purchase_url: e.target.value,
                            },
                          }))
                        }
                        className="rounded border px-3 py-2 text-sm"
                      />
                    </div>
                    <textarea
                      placeholder="Description"
                      value={component.new_item?.description || ''}
                      onChange={(e) =>
                        handleUpdateNewItemComponent(component.id, (prev) => ({
                          ...prev,
                          new_item: {
                            ...prev.new_item,
                            description: e.target.value,
                          },
                        }))
                      }
                      className="w-full rounded border px-3 py-2 text-sm"
                      rows={2}
                    />
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <input
                        type="text"
                        placeholder="Label de version (ex: v1.0)"
                        value={component.new_item?.version?.label || ''}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            new_item: {
                              ...prev.new_item,
                              version: {
                                ...prev.new_item?.version,
                                label: e.target.value,
                              },
                            },
                          }))
                        }
                        className="rounded border px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Note de version"
                        value={component.new_item?.version?.description || ''}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            new_item: {
                              ...prev.new_item,
                              version: {
                                ...prev.new_item?.version,
                                description: e.target.value,
                              },
                            },
                          }))
                        }
                        className="rounded border px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <input
                        type="number"
                        min="1"
                        value={component.quantity}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            quantity: e.target.value,
                          }))
                        }
                        className="rounded border px-3 py-2 text-sm"
                        placeholder="Quantite"
                      />
                      <input
                        type="text"
                        value={component.notes || ''}
                        onChange={(e) =>
                          handleUpdateNewItemComponent(component.id, (prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        className="rounded border px-3 py-2 text-sm"
                        placeholder="Note (optionnel)"
                      />
                    </div>
                    <div className="rounded-lg border border-slate-200/70 bg-slate-50 p-3 space-y-2">
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">
                        Sous-items du sous-item
                      </div>
                      {renderComponentList(
                        component.new_item?.components || [],
                        component.id,
                        depth + 1,
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-[11px] text-gray-400">Aucun sous-item.</div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleAddNewItemComponent(parentId, 'existing')}
            className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
          >
            Ajouter un sous-item existant
          </button>
          <button
            type="button"
            onClick={() => handleAddNewItemComponent(parentId, 'new')}
            className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-100"
          >
            Creer un sous-item
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
{showNewItemModal && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={() => setShowNewItemModal(false)}
  >
    <div
      className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase text-gray-500">Nouvel item</h2>
        <button
          type="button"
          onClick={() => setShowNewItemModal(false)}
          className="rounded p-1 text-gray-500 transition hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <form onSubmit={handleCreateItem} className="mt-4 space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              Nom de l item
            </div>
            <input
              type="text"
              placeholder="Ex: Capteur hydrostatique"
              value={newItem.name}
              onChange={(e) => setNewItem((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded border px-3 py-2 text-sm"
              required
            />
          </div>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              Lien (optionnel)
            </div>
            <input
              type="text"
              placeholder="https://"
              value={newItem.purchase_url}
              onChange={(e) =>
                setNewItem((prev) => ({ ...prev, purchase_url: e.target.value }))
              }
              className="w-full rounded border px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {[['reference', 'Référence', 'Réf. fabricant ou fournisseur'], ['supplier', 'Fournisseur', "Chez qui on l'achète"],
            ['unit_price', 'Prix unitaire HT (€)', '0,00']].map(([field, label, placeholder]) => (
            <div key={field} className="space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
              <input
                type={field === 'unit_price' ? 'number' : 'text'}
                step={field === 'unit_price' ? '0.01' : undefined}
                placeholder={placeholder}
                value={newItem[field] ?? ''}
                onChange={(e) => setNewItem((prev) => ({ ...prev, [field]: e.target.value }))}
                className="w-full rounded border px-3 py-2 text-sm"
              />
            </div>
          ))}
        </div>
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">
            Description
          </div>
          <textarea
            placeholder="Notes ou description courte"
            value={newItem.description}
            onChange={(e) =>
              setNewItem((prev) => ({ ...prev, description: e.target.value }))
            }
            className="w-full rounded border px-3 py-2 text-sm"
            rows={3}
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">
            Version initiale (optionnel)
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <input
              type="text"
              placeholder="Label de version (ex: v1.0)"
              value={newItemVersion.label}
              onChange={(e) =>
                setNewItemVersion((prev) => ({ ...prev, label: e.target.value }))
              }
              className="rounded border px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Note de version"
              value={newItemVersion.description}
              onChange={(e) =>
                setNewItemVersion((prev) => ({ ...prev, description: e.target.value }))
              }
              className="rounded border px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              Sous-items initiaux (optionnel)
            </div>
            {newItemComponentCount > 0 && (
              <span className="text-[11px] text-gray-400">
                {newItemComponentCount} sous-items
              </span>
            )}
          </div>
          {renderComponentList(newItemComponents)}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Creer l item
          </button>
          <button
            type="button"
            onClick={() => setShowNewItemModal(false)}
            className="rounded border px-3 py-2 text-sm text-gray-600 hover:border-gray-300"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{showQuickStockModal && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={() => setShowQuickStockModal(false)}
  >
    <div
      className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase text-gray-500">Ajout rapide</h2>
          <p className="text-xs text-gray-500">
            Pour un achat simple : cree l item et ajoute une occurrence.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowQuickStockModal(false)}
          className="rounded p-1 text-gray-500 transition hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <form onSubmit={handleQuickStockCreate} className="mt-4 space-y-2">
        <input
          type="text"
          placeholder="Nom de l item"
          value={quickStock.item_name}
          onChange={(e) =>
            setQuickStock((prev) => ({ ...prev, item_name: e.target.value }))
          }
          className="w-full rounded border px-3 py-2 text-sm"
          required
        />
        <input
          type="text"
          placeholder="Nom de l occurrence (optionnel)"
          value={quickStock.occurrence_name}
          onChange={(e) =>
            setQuickStock((prev) => ({ ...prev, occurrence_name: e.target.value }))
          }
          className="w-full rounded border px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Version (optionnel)"
          value={quickStock.version_label}
          onChange={(e) =>
            setQuickStock((prev) => ({ ...prev, version_label: e.target.value }))
          }
          className="w-full rounded border px-3 py-2 text-sm"
        />
        <textarea
          placeholder="Description courte"
          value={quickStock.description}
          onChange={(e) =>
            setQuickStock((prev) => ({ ...prev, description: e.target.value }))
          }
          className="w-full rounded border px-3 py-2 text-sm"
          rows={2}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select
            value={quickStock.status}
            onChange={(e) => setQuickStock((prev) => ({ ...prev, status: e.target.value }))}
            className="rounded border px-3 py-2 text-sm"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Emplacement"
            value={quickStock.location}
            onChange={(e) =>
              setQuickStock((prev) => ({ ...prev, location: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="number"
            min="1"
            placeholder="Quantite"
            value={quickStock.quantity}
            onChange={(e) =>
              setQuickStock((prev) => ({ ...prev, quantity: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
          />
          <div className="text-[11px] text-gray-400">
            Cree autant d occurrences que necessaire.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select
            value={quickStock.client_id}
            onChange={(e) =>
              setQuickStock((prev) => ({ ...prev, client_id: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
          >
            <option value="">Proprietaire (optionnel)</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          <select
            value={quickStock.project_id}
            onChange={(e) =>
              setQuickStock((prev) => ({ ...prev, project_id: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
          >
            <option value="">Projet (optionnel)</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
        {quickStock.status === 'ordered' && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              Numero de suivi
            </div>
            <input
              type="text"
              placeholder="Numero de suivi"
              value={quickStock.tracking_number}
              onChange={(e) =>
                setQuickStock((prev) => ({ ...prev, tracking_number: e.target.value }))
              }
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
          </div>
        )}
        <textarea
          placeholder="Notes"
          value={quickStock.notes}
          onChange={(e) => setQuickStock((prev) => ({ ...prev, notes: e.target.value }))}
          className="w-full rounded border px-3 py-2 text-sm"
          rows={2}
        />
        <div className="flex items-center gap-2">
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
            disabled={loading.quick}
          >
            <Plus className="h-4 w-4" />
            {loading.quick ? 'En cours...' : 'Ajouter au stock'}
          </button>
          <button
            type="button"
            onClick={() => setShowQuickStockModal(false)}
            className="rounded border px-3 py-2 text-sm text-gray-600 hover:border-gray-300"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{showExistingStockModal && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={() => setShowExistingStockModal(false)}
  >
    <div
      className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase text-gray-500">
            Occurrence existante
          </h2>
          <p className="text-xs text-gray-500">
            Choisissez l item et la version deja definie.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowExistingStockModal(false)}
          className="rounded p-1 text-gray-500 transition hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <form onSubmit={handleCreateStock} className="mt-4 space-y-2">
        <div className="relative">
          <input
            type="text"
            value={itemSearch}
            onChange={handleItemInputChange}
            onFocus={openItemDropdown}
            onBlur={() => setTimeout(() => closeItemDropdown(), 120)}
            placeholder="Selectionner ou rechercher un item"
            className="w-full rounded border px-3 py-2 pl-9 text-sm"
            aria-autocomplete="list"
            aria-expanded={itemDropdownOpen}
          />
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          {itemDropdownOpen && (
            <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {filteredItems.length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-500">
                  Aucun item ne correspond a "{itemSearch || ''}".
                </div>
              ) : (
                filteredItems.map((item) => {
                  const isSelected = String(stockFormItemId) === String(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition ${
                        isSelected
                          ? 'bg-blue-50 text-blue-700'
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleItemSelect(item)}
                    >
                      <span className="flex flex-col">
                        <span className="font-medium">{item.name}</span>
                        {item.description && (
                          <span className="text-xs text-slate-500">
                            {item.description}
                          </span>
                        )}
                      </span>
                      {isSelected && (
                        <span className="text-xs font-semibold text-blue-500">
                          Selectionne
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
        <select
          value={newStock.item_version_id}
          onChange={(e) =>
            setNewStock((prev) => ({ ...prev, item_version_id: e.target.value }))
          }
          className="rounded border px-3 py-2 text-sm"
          disabled={!stockFormItemId}
          required
        >
          <option value="">Version</option>
          {stockFormVersions.map((version) => (
            <option key={version.id} value={version.id}>
              V{version.version_number} {version.label ? `- ${version.label}` : ''}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Nom de l occurrence (optionnel)"
          value={newStock.occurrence_name}
          onChange={(e) =>
            setNewStock((prev) => ({ ...prev, occurrence_name: e.target.value }))
          }
          className="rounded border px-3 py-2 text-sm"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select
            value={newStock.status}
            onChange={(e) => setNewStock((prev) => ({ ...prev, status: e.target.value }))}
            className="rounded border px-3 py-2 text-sm"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Emplacement"
            value={newStock.location}
            onChange={(e) =>
              setNewStock((prev) => ({ ...prev, location: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="number"
            min="1"
            placeholder="Quantite"
            value={newStock.quantity}
            onChange={(e) =>
              setNewStock((prev) => ({ ...prev, quantity: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
            disabled={selectedChildStockIds.length > 0}
          />
          <div className="text-[11px] text-gray-400">
            {selectedChildStockIds.length > 0
              ? 'Quantite desactivee si vous liez des sous-items existants.'
              : 'Indiquez combien d occurrences creer.'}
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={newStock.create_children}
            onChange={(e) => {
              const shouldCreate = e.target.checked;
              setNewStock((prev) => ({ ...prev, create_children: shouldCreate }));
              if (!shouldCreate) {
                setSelectedChildStockIds([]);
              }
            }}
          />
          Creer les sous-items automatiquement
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select
            value={newStock.client_id}
            onChange={(e) =>
              setNewStock((prev) => ({ ...prev, client_id: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
          >
            <option value="">Proprietaire (optionnel)</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          <select
            value={newStock.project_id}
            onChange={(e) =>
              setNewStock((prev) => ({ ...prev, project_id: e.target.value }))
            }
            className="rounded border px-3 py-2 text-sm"
          >
            <option value="">Projet (optionnel)</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
        <div className="rounded border bg-gray-50 p-3 text-xs space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              Sous-items compatibles (optionnel)
            </div>
            {loadingLinkable && (
              <span className="text-[11px] text-gray-400">Chargement...</span>
            )}
          </div>
          {!newStock.create_children && (
            <div className="text-[11px] text-gray-400">
              Activez la creation des sous-items pour lier des existants.
            </div>
          )}
          <input
            type="text"
            placeholder="Rechercher un item a lier"
            value={linkableSearch}
            onChange={(e) => setLinkableSearch(e.target.value)}
            className="w-full rounded border px-2 py-1 text-xs"
            disabled={!newStock.create_children}
          />
          <div className="max-h-32 space-y-1 overflow-auto">
            {filteredLinkableStockItems.length === 0 && (
              <div className="text-[11px] text-gray-400">Aucun item disponible.</div>
            )}
            {filteredLinkableStockItems.map((stockItem) => {
              const isSelected = selectedChildStockIds.includes(stockItem.id);
              return (
                <label
                  key={stockItem.id}
                  className="flex items-center gap-2 rounded border bg-white px-2 py-1"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() =>
                      setSelectedChildStockIds((prev) =>
                        isSelected
                          ? prev.filter((id) => id !== stockItem.id)
                          : [...prev, stockItem.id],
                      )
                    }
                    disabled={!newStock.create_children}
                  />
                  <span className="text-xs text-gray-700">
                    {stockItem.serial_number || `#${stockItem.id}`} - {stockItem.item_name}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {getStatusLabel(stockItem.status)}
                  </span>
                </label>
              );
            })}
          </div>
          {selectedChildStockIds.length > 0 && (
            <div className="text-[11px] text-gray-500">
              Les sous-items lies ne seront plus visibles dans la liste principale.
            </div>
          )}
        </div>
        {newStock.status === 'ordered' && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              Numero de suivi
            </div>
            <input
              type="text"
              placeholder="Numero de suivi"
              value={newStock.tracking_number}
              onChange={(e) =>
                setNewStock((prev) => ({ ...prev, tracking_number: e.target.value }))
              }
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {newStock.status === 'ordered' && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-400">
                Date de commande
              </div>
              <input
                type="datetime-local"
                value={newStock.ordered_at}
                onChange={(e) =>
                  setNewStock((prev) => ({ ...prev, ordered_at: e.target.value }))
                }
                className="mt-1 rounded border px-3 py-2 text-sm w-full"
              />
            </div>
          )}
          {newStock.status === 'in_stock' && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-400">
                Date de reception
              </div>
              <input
                type="datetime-local"
                value={newStock.received_at}
                onChange={(e) =>
                  setNewStock((prev) => ({ ...prev, received_at: e.target.value }))
                }
                className="mt-1 rounded border px-3 py-2 text-sm w-full"
              />
            </div>
          )}
          {newStock.status === 'at_client' && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-400">
                Date d envoi
              </div>
              <input
                type="datetime-local"
                value={newStock.shipped_at}
                onChange={(e) =>
                  setNewStock((prev) => ({ ...prev, shipped_at: e.target.value }))
                }
                className="mt-1 rounded border px-3 py-2 text-sm w-full"
              />
            </div>
          )}
          {newStock.status === 'removed' && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-400">
                Date de suppression
              </div>
              <input
                type="datetime-local"
                value={newStock.removed_at}
                onChange={(e) =>
                  setNewStock((prev) => ({ ...prev, removed_at: e.target.value }))
                }
                className="mt-1 rounded border px-3 py-2 text-sm w-full"
              />
            </div>
          )}
        </div>
        <textarea
          placeholder="Notes"
          value={newStock.notes}
          onChange={(e) => setNewStock((prev) => ({ ...prev, notes: e.target.value }))}
          className="rounded border px-3 py-2 text-sm"
          rows={2}
        />
        <div className="flex items-center gap-2">
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Ajouter l occurrence
          </button>
          <button
            type="button"
            onClick={() => setShowExistingStockModal(false)}
            className="rounded border px-3 py-2 text-sm text-gray-600 hover:border-gray-300"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{showQrModal && qrStockItem && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={closeQrModal}
  >
    <div
      className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold uppercase text-gray-500">QR code</h2>
          <p className="text-xs text-gray-500">
            {qrStockItem.serial_number || `#${qrStockItem.id}`} - {qrStockItem.item_name}
          </p>
          <p className="text-xs text-gray-400">
            {qrStockItem.version_number ? `V${qrStockItem.version_number}` : ''}
            {qrStockItem.version_label ? ` ${qrStockItem.version_label}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={closeQrModal}
          className="rounded p-1 text-gray-500 transition hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-4 flex flex-col items-center gap-3">
        <div className="flex h-60 w-60 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
          {qrLoading ? (
            <span className="text-xs text-slate-400">Generation...</span>
          ) : (
            qrDataUrl && (
              <img
                src={qrDataUrl}
                alt="QR code"
                className="h-52 w-52 rounded-xl bg-white p-2 shadow-sm"
              />
            )
          )}
        </div>
        <input
          type="text"
          readOnly
          value={getStockShareUrl(qrStockItem)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-500"
        />
        <div className="flex items-center gap-2">
          <a
            href={qrDataUrl || '#'}
            download={`qr-${qrStockItem.serial_number || qrStockItem.id}.png`}
            className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${
              qrDataUrl
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'cursor-not-allowed bg-slate-200 text-slate-400'
            }`}
            onClick={(e) => {
              if (!qrDataUrl) e.preventDefault();
            }}
          >
            <Download className="h-4 w-4" />
            Telecharger PNG
          </a>
          <button
            type="button"
            onClick={closeQrModal}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-slate-300"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{showStockEditModal && editingStockItem && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={closeStockEditModal}
  >
    <div
      className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold uppercase text-gray-500">
            Editer l occurrence
          </h2>
          <p className="text-xs text-gray-500">
            {editingStockItem.serial_number || `#${editingStockItem.id}`} -{' '}
            {editingStockItem.item_name}
            {editingStockItem.occurrence_name
              ? ` - ${editingStockItem.occurrence_name}`
              : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={closeStockEditModal}
          className="rounded p-1 text-gray-500 transition hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateStock(editingStockItem.id);
        }}
        className="mt-4 space-y-2 text-sm"
      >
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <label className="space-y-1 md:col-span-3">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              Nom de l occurrence
            </span>
            <input
              type="text"
              value={stockEdits.occurrence_name || ''}
              onChange={(e) =>
                setStockEdits((prev) => ({ ...prev, occurrence_name: e.target.value }))
              }
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Optionnel"
            />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              Statut
            </span>
            <select
              value={stockEdits.status || ''}
              onChange={(e) =>
                setStockEdits((prev) => ({ ...prev, status: e.target.value }))
              }
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              Emplacement
            </span>
            <input
              type="text"
              value={stockEdits.location || ''}
              onChange={(e) =>
                setStockEdits((prev) => ({ ...prev, location: e.target.value }))
              }
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </label>
          {stockEdits.status === 'ordered' && (
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                Numero de suivi
              </span>
              <input
                type="text"
                value={stockEdits.tracking_number || ''}
                onChange={(e) =>
                  setStockEdits((prev) => ({
                    ...prev,
                    tracking_number: e.target.value,
                  }))
                }
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
          )}
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              Proprietaire
            </span>
            <select
              value={stockEdits.client_id || ''}
              onChange={(e) =>
                setStockEdits((prev) => ({ ...prev, client_id: e.target.value }))
              }
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">Aucun</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              Projet
            </span>
            <select
              value={stockEdits.project_id || ''}
              onChange={(e) =>
                setStockEdits((prev) => ({ ...prev, project_id: e.target.value }))
              }
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">Aucun</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          {stockEdits.status === 'ordered' && (
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                Date de commande
              </span>
              <input
                type="datetime-local"
                value={stockEdits.ordered_at || ''}
                onChange={(e) =>
                  setStockEdits((prev) => ({ ...prev, ordered_at: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
          )}
          {stockEdits.status === 'in_stock' && (
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                Date de reception
              </span>
              <input
                type="datetime-local"
                value={stockEdits.received_at || ''}
                onChange={(e) =>
                  setStockEdits((prev) => ({ ...prev, received_at: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
          )}
          {stockEdits.status === 'at_client' && (
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                Date d envoi
              </span>
              <input
                type="datetime-local"
                value={stockEdits.shipped_at || ''}
                onChange={(e) =>
                  setStockEdits((prev) => ({ ...prev, shipped_at: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
          )}
          {stockEdits.status === 'removed' && (
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                Date de suppression
              </span>
              <input
                type="datetime-local"
                value={stockEdits.removed_at || ''}
                onChange={(e) =>
                  setStockEdits((prev) => ({ ...prev, removed_at: e.target.value }))
                }
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
          )}
        </div>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            Notes
          </span>
          <textarea
            value={stockEdits.notes || ''}
            onChange={(e) =>
              setStockEdits((prev) => ({ ...prev, notes: e.target.value }))
            }
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            rows={3}
          />
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleDeleteStock(editingStockItem)}
            className="inline-flex items-center gap-2 rounded border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </button>
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm text-white"
          >
            Sauvegarder
          </button>
          <button
            type="button"
            onClick={closeStockEditModal}
            className="rounded border px-3 py-2 text-sm text-gray-600 hover:border-gray-300"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  </div>
)}
    </>
  );
};

export default InventoryModals;
